/* @flow */

import clsx from 'clsx';
import React, { useCallback, useRef } from 'react';

import './RowSetting.css';
import { Setting } from '../../helpers/settings/types';
import Toggle, { type ToggleHandle } from '../toggle/Toggle';

type RowSettingTogglePropType = {
  +'aria-label'?: string,
  +checked: boolean,
  +children: React.Node,
  +className?: string,
  +disabled?: boolean,
  +onChange: (checked: boolean, setting?: Setting) => Promise<void> | void,
  +setting?: Setting,
};

const RowSettingToggle = (rowSettingToggleProps: RowSettingTogglePropType): React.Node => {
  const { 'aria-label': ariaLabel, checked, children, className = '', disabled, onChange, setting } = rowSettingToggleProps;

  const toggleRef = useRef<ToggleHandle | null>(null);

  const [unfocused, setUnfocused] = React.useState(true);

  const toggle = useCallback(() => toggleRef.current?.click(), [toggleRef.current]);

  const handleOnClick = useCallback(() => toggle(), [toggle]);

  const handleOnKeyDown = useCallback(
    (event: SyntheticKeyboardEvent<HTMLElement>) => {
      const { key } = event;

      if (key === 'Enter' || key === ' ') {
        event.preventDefault();
        toggle();
      }
    },
    [toggle],
  );

  const handleOnMouseEnter = useCallback(() => setUnfocused(false), []);

  const handleOnMouseLeave = useCallback(() => setUnfocused(true), []);

  return (
    <div
      aria-checked={checked}
      aria-label={ariaLabel}
      className={clsx('rowSetting', className, disabled && 'disabled')}
      onClick={!disabled ? handleOnClick : undefined}
      onKeyDown={!disabled ? handleOnKeyDown : undefined}
      onMouseEnter={handleOnMouseEnter}
      onMouseLeave={handleOnMouseLeave}
      role='switch'
      tabIndex='0'
    >
      <div className='rowSettingLabel'>{children}</div>
      <Toggle checked={checked} className='rowSettingDisable' onChange={onChange} ref={toggleRef} setting={setting} unfocused={unfocused} />
    </div>
  );
};

export default RowSettingToggle;
